"use client";

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { getResources, type ResourceItem } from "@/lib/resources";

export default function ArticlesPage() {
  const [articles, setArticles] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getResources("current_affairs" as any, true);
        setArticles(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = searchQuery.trim().toLowerCase();
    return articles
      .filter(i => i.isVisible !== false)
      .filter((i) => (i.title || "").toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s));
  }, [articles, searchQuery]);

  return (
    <div className="min-h-screen bg-slate-50/50">
      <section className="bg-white border-b border-slate-100 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6 font-display">
            Study <span className="text-primary ">Articles</span> & Notes
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-10">
            Deep dive into subject-specific notes and daily current affairs analysis.
          </p>

          <div className="max-w-xl mx-auto relative group">
            <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary transition-colors">
              <Search size={22} />
            </div>
            <input
              type="text"
              placeholder="Search articles..."
              className="w-full h-16 pl-14 pr-6 rounded-2xl bg-white border-2 border-slate-100 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all text-lg font-medium shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="animate-spin text-primary" size={48} />
            <p className="text-slate-500 font-bold">Loading articles...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {filtered.map((a) => (
              <div key={a.id} className="bg-white rounded-[2rem] overflow-hidden border border-slate-100 p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{a.category || "General"}</span>
                  {a.link ? (
                    <a className="text-xs font-bold text-primary hover:underline" href={a.link} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : null}
                </div>
                <h3 className="text-xl font-black text-slate-900 leading-tight mb-3 line-clamp-2">{a.title}</h3>
                <p className="text-slate-500 font-medium leading-relaxed line-clamp-3">{a.description}</p>
                <div className="pt-6 mt-6 border-t border-slate-50">
                  <Link to="/login" className="text-xs font-bold text-slate-500 hover:text-primary">
                    Log in to bookmark / track
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

